import React, { useState, useMemo } from "react";
import PropTypes from "prop-types";
import { useCommentsAndLocations } from "../../hooks/commentsAndResidenceQuery";
import {
  useNotification,
  NotificationSeverity,
} from "../../context/NotificationContext";
import { useQueryClient } from "@tanstack/react-query";
import CommentsLocationsDrawer from "../../components/ui/drawner/CommentsLocationsDrawer";
import EditIcon from "../../assets/svg/view/edit.svg";
import EditLightIcon from "../../assets/svg/view/edit_ligh.svg";
import { ReactComponent as LinkIcon } from "../../assets/svg/components/calendar.svg";
import { ReactComponent as UnlinkIcon } from "../../assets/svg/components/calendar_light.svg";
import VehicleStreamingMediaType from "../../utils/VehicleStreamingMedia";
import "../../assets/scss/components/vehicle/VehicleProfileInfo.scss";

const VehicleProfileInfo = ({
  vehicle,
  theme,
  onEditClick,
  showRelatedVehicle,
  isSimpleVehicle,
}) => {
  const { showNotification } = useNotification();
  const queryClient = useQueryClient();
  const {
    useCommentsByReference,
    createComment,
    updateComment,
    deleteComment,
  } = useCommentsAndLocations();

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerType, setDrawerType] = useState(null);

  const { data: comments } = useCommentsByReference(vehicle?.id);
  const commentData = comments?.data || [];

  // Determinar si el vehículo requiere trailer/cabezote
  const vehicleRequiresRelated = useMemo(() => {
    if (!vehicle) return false;
    const typesWithTrailer = ["TRACTOCAMION", "CAMA ALTA", "CARGADOR"];
    return (
      typesWithTrailer.includes(vehicle.type_name?.toUpperCase()) ||
      vehicle.is_trailer
    );
  }, [vehicle]);

  // Determinar si tiene vehículo relacionado
  const hasRelatedVehicle = useMemo(() => {
    return vehicle?.relatedVehicle && vehicle.relatedVehicle.data;
  }, [vehicle]);

  // Obtener el tipo de vehículo relacionado para mostrar en la UI
  const getRelatedVehicleTypeLabel = useMemo(() => {
    if (!vehicle || !hasRelatedVehicle) return "";

    return vehicle.relatedVehicle.type === "trailer"
      ? "Trailer"
      : "Cabezote";
  }, [vehicle, hasRelatedVehicle]);

  // Funciones de utilidad para formateo de datos
  const formatPhoneNumber = (countryCode, phoneNumber) => {
    if (!phoneNumber) return "No disponible";
    return `${countryCode || ""} ${phoneNumber}`.trim();
  };

  const formatFullName = (person) => {
    if (!person) return "No asignado";
    const { first_name, first_last_name, second_last_name } = person;
    return `${first_name || ""} ${first_last_name || ""} ${
      second_last_name || ""
    }`.trim();
  };

  const formatDate = (dateString) => {
    if (!dateString) return "No disponible";
    try {
      return new Date(dateString).toLocaleDateString("es-CO", {
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    } catch {
      return "Fecha inválida";
    }
  };

  // Determinar tipo de vehículo y mostrar información relevante
  const getVehicleTypeDisplay = () => {
    if (vehicle.is_trailer) {
      return "Trailer";
    }
    return vehicle.type_name || "Cabezote";
  };

  // Determinar si debe mostrar información de operador
  const shouldShowOperatorSection = () => {
    // Los trailers no tienen operador propio, usan el del cabezote
    return !vehicle.is_trailer;
  };

  // Gestión de drawer y comentarios
  const handleOpenDrawer = (type) => {
    setDrawerType(type);
    setDrawerOpen(true);
  };

  const handleEdit = async (id, item) => {
    try {
      await updateComment({
        commentId: id,
        data: { ...item, reference_id: vehicle?.id },
      });
      showNotification({
        message: "Comentario actualizado exitosamente",
        severity: NotificationSeverity.SUCCESS,
      });
      queryClient.invalidateQueries(["comments", vehicle?.id]);
    } catch (error) {
      console.error("Error al editar:", error);
      showNotification({
        message:
          error.response?.data?.message || "Error al actualizar el comentario",
        severity: NotificationSeverity.ERROR,
      });
    }
  };

  const handleDelete = async (id) => {
    try {
      await deleteComment(id);
      showNotification({
        message: "Comentario eliminado exitosamente",
        severity: NotificationSeverity.SUCCESS,
      });
      queryClient.invalidateQueries(["comments", vehicle?.id]);
    } catch (error) {
      console.error("Error al eliminar:", error);
      showNotification({
        message:
          error.response?.data?.message || "Error al eliminar el comentario",
        severity: NotificationSeverity.ERROR,
      });
    }
  };

  const handleCreate = async (item) => {
    try {
      await createComment({ ...item, reference_id: vehicle?.id });
      showNotification({
        message: "Comentario creado exitosamente",
        severity: NotificationSeverity.SUCCESS,
      });
      queryClient.invalidateQueries(["comments", vehicle?.id]);
    } catch (error) {
      console.error("Error al crear:", error);
      showNotification({
        message:
          error.response?.data?.message || "Error al crear el comentario",
        severity: NotificationSeverity.ERROR,
      });
    }
  };

  const handleCloseDrawer = () => {
    setDrawerOpen(false);
  };

  // Selección dinámica del ícono según el tema
  const editIconSrc = theme === "dark" ? EditLightIcon : EditIcon;

  return (
    <div className={`GM__${theme}-vehicle-profile-info`}>
      <button
        className={`GM__${theme}-edit-button`}
        onClick={onEditClick}
        title="Editar vehículo"
      >
        <img src={editIconSrc} alt="Editar icon" />
      </button>

      <div className={`GM__${theme}-profile-container`}>
        {/* Header con imagen y badges */}
        <div className={`GM__${theme}-profile-header`}>
          <div className={`GM__${theme}-vehicle-image-container`}>
            <div className={`GM__${theme}-image-circle`}>
              <VehicleStreamingMediaType
                url={vehicle.id}
                type="vehiclePhoto"
                className={`GM__${theme}-vehicle-image`}
              />
            </div>
          </div>

          <div className={`GM__${theme}-header-content`}>
            <h2 className={`GM__${theme}-vehicle-title`}>
              {getVehicleTypeDisplay()}
            </h2>
            <div className={`GM__${theme}-title-underline`}></div>
            <div className={`GM__${theme}-badges-container`}>
              <div className={`GM__${theme}-plate-badge`}>
                {vehicle.plate || "PLACA"}
              </div>
              <div className={`GM__${theme}-type-badge`}>
                {vehicle.type_name || "Tipo de vehículo"}
              </div>
            </div>
          </div>
        </div>

        {/* Sección de contrato */}
        <div className={`GM__${theme}-contract-section`}>
          <h3 className={`GM__${theme}-section-title`}>
            Información de Contrato
          </h3>
          <div className={`GM__${theme}-contract-content`}>
            <div className={`GM__${theme}-contract-field-group`}>
              <div className={`GM__${theme}-contract-field`}>
                <span className={`GM__${theme}-contract-label`}>
                  Fecha de inicio:
                </span>
                <span className={`GM__${theme}-contract-value`}>
                  {formatDate(vehicle.contract_start_date)}
                </span>
              </div>
              <div className={`GM__${theme}-contract-field`}>
                <span className={`GM__${theme}-contract-label`}>
                  Fecha de fin:
                </span>
                <span className={`GM__${theme}-contract-value`}>
                  {formatDate(vehicle.contract_end_date)}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Grid de información */}
        <div className={`GM__${theme}-info-grid`}>
          {/* Información básica del vehículo */}
          <div className={`GM__${theme}-info-card`}>
            <h3 className={`GM__${theme}-card-title`}>
              Información del vehículo
            </h3>
            <div className={`GM__${theme}-card-content`}>
              <div className={`GM__${theme}-field-group`}>
                <div className={`GM__${theme}-field`}>
                  <span className={`GM__${theme}-field-label`}>Marca:</span>
                  <span className={`GM__${theme}-field-value`}>
                    {vehicle.brand_name || "No disponible"}
                  </span>
                </div>
                <div className={`GM__${theme}-field`}>
                  <span className={`GM__${theme}-field-label`}>Modelo:</span>
                  <span className={`GM__${theme}-field-value`}>
                    {vehicle.model || "No disponible"}
                  </span>
                </div>
              </div>
              <div className={`GM__${theme}-field-group`}>
                <div className={`GM__${theme}-field`}>
                  <span className={`GM__${theme}-field-label`}>Año:</span>
                  <span className={`GM__${theme}-field-value`}>
                    {vehicle.year || "No disponible"}
                  </span>
                </div>
                <div className={`GM__${theme}-field`}>
                  <span className={`GM__${theme}-field-label`}>Capacidad:</span>
                  <span className={`GM__${theme}-field-value`}>
                    {vehicle.capacity
                      ? `${vehicle.capacity} ton`
                      : "No disponible"}
                  </span>
                </div>
              </div>
              <div className={`GM__${theme}-field-group`}>
                <div className={`GM__${theme}-field`}>
                  <span className={`GM__${theme}-field-label`}>Color:</span>
                  <span className={`GM__${theme}-field-value`}>
                    {vehicle.color || "No disponible"}
                  </span>
                </div>
                <div className={`GM__${theme}-field`}>
                  <span className={`GM__${theme}-field-label`}>
                    Nacionalidad:
                  </span>
                  <span className={`GM__${theme}-field-value`}>
                    {vehicle.nationality_country || "No disponible"}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Información del propietario */}
          <div className={`GM__${theme}-info-card`}>
            <h3 className={`GM__${theme}-card-title`}>
              Información del propietario
            </h3>
            <div className={`GM__${theme}-card-content`}>
              {vehicle.owner ? (
                <>
                  <div className={`GM__${theme}-person-info`}>
                    <div className={`GM__${theme}-field-group`}>
                      <div className={`GM__${theme}-field-full`}>
                        <span className={`GM__${theme}-field-label`}>
                          Nombre completo:
                        </span>
                        <span className={`GM__${theme}-field-value`}>
                          {formatFullName(vehicle.owner)}
                        </span>
                      </div>
                    </div>
                    <div className={`GM__${theme}-field-group`}>
                      <div className={`GM__${theme}-field`}>
                        <span className={`GM__${theme}-field-label`}>
                          Documento:
                        </span>
                        <span className={`GM__${theme}-field-value`}>
                          {vehicle.owner.document_number || "No disponible"}
                        </span>
                      </div>
                      <div className={`GM__${theme}-field`}>
                        <span className={`GM__${theme}-field-label`}>
                          Email:
                        </span>
                        <span className={`GM__${theme}-field-value`}>
                          {vehicle.owner.email || "No disponible"}
                        </span>
                      </div>
                    </div>
                    <div className={`GM__${theme}-field-group`}>
                      <div className={`GM__${theme}-field`}>
                        <span className={`GM__${theme}-field-label`}>
                          Teléfono principal:
                        </span>
                        <span className={`GM__${theme}-field-value`}>
                          {formatPhoneNumber(
                            vehicle.owner.primary_phone_code,
                            vehicle.owner.primary_phone_number
                          )}
                        </span>
                      </div>
                      <div className={`GM__${theme}-field`}>
                        <span className={`GM__${theme}-field-label`}>
                          Teléfono secundario:
                        </span>
                        <span className={`GM__${theme}-field-value`}>
                          {formatPhoneNumber(
                            vehicle.owner.secondary_phone_code,
                            vehicle.owner.secondary_phone_number
                          )}
                        </span>
                      </div>
                    </div>
                  </div>
                </>
              ) : (
                <div className={`GM__${theme}-no-data`}>
                  <span className={`GM__${theme}-no-data-text`}>
                    No hay propietario asignado
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Información del operador - Solo si no es trailer */}
          {shouldShowOperatorSection() && (
            <div className={`GM__${theme}-info-card`}>
              <h3 className={`GM__${theme}-card-title`}>
                Información del operador
              </h3>
              <div className={`GM__${theme}-card-content`}>
                {vehicle.operator ? (
                  <>
                    <div className={`GM__${theme}-person-info`}>
                      <div className={`GM__${theme}-field-group`}>
                        <div className={`GM__${theme}-field-full`}>
                          <span className={`GM__${theme}-field-label`}>
                            Nombre completo:
                          </span>
                          <span className={`GM__${theme}-field-value`}>
                            {formatFullName(vehicle.operator)}
                          </span>
                        </div>
                      </div>
                      <div className={`GM__${theme}-field-group`}>
                        <div className={`GM__${theme}-field`}>
                          <span className={`GM__${theme}-field-label`}>
                            Documento:
                          </span>
                          <span className={`GM__${theme}-field-value`}>
                            {vehicle.operator.document_number ||
                              "No disponible"}
                          </span>
                        </div>
                        <div className={`GM__${theme}-field`}>
                          <span className={`GM__${theme}-field-label`}>
                            Email:
                          </span>
                          <span className={`GM__${theme}-field-value`}>
                            {vehicle.operator.email || "No disponible"}
                          </span>
                        </div>
                      </div>
                      <div className={`GM__${theme}-field-group`}>
                        <div className={`GM__${theme}-field`}>
                          <span className={`GM__${theme}-field-label`}>
                            Teléfono principal:
                          </span>
                          <span className={`GM__${theme}-field-value`}>
                            {formatPhoneNumber(
                              vehicle.operator.primary_phone_code,
                              vehicle.operator.primary_phone_number
                            )}
                          </span>
                        </div>
                        <div className={`GM__${theme}-field`}>
                          <span className={`GM__${theme}-field-label`}>
                            Teléfono secundario:
                          </span>
                          <span className={`GM__${theme}-field-value`}>
                            {formatPhoneNumber(
                              vehicle.operator.secondary_phone_code,
                              vehicle.operator.secondary_phone_number
                            )}
                          </span>
                        </div>
                      </div>
                    </div>
                  </>
                ) : (
                  <div className={`GM__${theme}-no-data`}>
                    <span className={`GM__${theme}-no-data-text`}>
                      No hay operador asignado
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Información de contacto del vehículo */}
          <div className={`GM__${theme}-info-card`}>
            <h3 className={`GM__${theme}-card-title`}>Contacto del vehículo</h3>
            <div className={`GM__${theme}-card-content`}>
              <div className={`GM__${theme}-field-group`}>
                <div className={`GM__${theme}-field`}>
                  <span className={`GM__${theme}-field-label`}>
                    Teléfono principal:
                  </span>
                  <span className={`GM__${theme}-field-value`}>
                    {formatPhoneNumber(
                      vehicle.primary_phone_code,
                      vehicle.primary_phone_number
                    )}
                  </span>
                </div>
                <div className={`GM__${theme}-field`}>
                  <span className={`GM__${theme}-field-label`}>
                    Teléfono secundario:
                  </span>
                  <span className={`GM__${theme}-field-value`}>
                    {formatPhoneNumber(
                      vehicle.secondary_phone_code,
                      vehicle.second_phone
                    )}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Información de operación y configuración */}
          <div className={`GM__${theme}-info-card`}>
            <h3 className={`GM__${theme}-card-title`}>
              Configuración y operación
            </h3>
            <div className={`GM__${theme}-card-content`}>
              <div className={`GM__${theme}-field-group`}>
                <div className={`GM__${theme}-field`}>
                  <span className={`GM__${theme}-field-label`}>Operación:</span>
                  <span className={`GM__${theme}-field-value`}>
                    {vehicle.operation_name || "No asignada"}
                  </span>
                </div>
                <div className={`GM__${theme}-field`}>
                  <span className={`GM__${theme}-field-label`}>
                    Pertenece a operación:
                  </span>
                  <span
                    className={`GM__${theme}-field-value ${
                      vehicle.belongs_to_operation ? "active" : "inactive"
                    }`}
                  >
                    {vehicle.belongs_to_operation ? "Sí" : "No"}
                  </span>
                </div>
              </div>
              <div className={`GM__${theme}-field-group`}>
                <div className={`GM__${theme}-field`}>
                  <span className={`GM__${theme}-field-label`}>
                    Tipo de categoría:
                  </span>
                  <span className={`GM__${theme}-field-value`}>
                    {vehicle.type_category || "No disponible"}
                  </span>
                </div>
                <div className={`GM__${theme}-field`}>
                  <span className={`GM__${theme}-field-label`}>
                    Es trailer:
                  </span>
                  <span className={`GM__${theme}-field-value`}>
                    {vehicle.is_trailer ? "Sí" : "No"}
                  </span>
                </div>
              </div>
              {vehicle.operation_id && (
                <div className={`GM__${theme}-field-group`}>
                  <div className={`GM__${theme}-field-full`}>
                    <span className={`GM__${theme}-field-label`}>
                      ID de operación:
                    </span>
                    <span className={`GM__${theme}-field-value mono`}>
                      {vehicle.operation_id}
                    </span>
                  </div>
                </div>
              )}
              {!vehicle.is_trailer && vehicle.vehicle_type_id !== 'fc52271b-d9a9-47ac-b734-8aaa38bf5edf' && (
                <div className={`GM__${theme}-field-group`}>
                  <div className={`GM__${theme}-field-full`}>
                    <span className={`GM__${theme}-field-label`}>
                      Proveedor GPS:
                    </span>
                    <span className={`GM__${theme}-field-value ${vehicle.gps_provider_name ? 'has-provider' : 'no-provider'}`}>
                      {vehicle.gps_provider_name ? (
                        <>
                          <span className={`GM__${theme}-gps-icon`}>📡</span>
                          {vehicle.gps_provider_name.charAt(0).toUpperCase() + vehicle.gps_provider_name.slice(1)}
                        </>
                      ) : (
                        <>
                          <span className={`GM__${theme}-no-gps-icon`}>⚠️</span>
                          Sin proveedor GPS asignado
                        </>
                      )}
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* SECCIÓN ACTUALIZADA: Información del vehículo relacionado */}
        {vehicleRequiresRelated && (
          <div className={`GM__${theme}-related-vehicle-section`}>
            <h3 className={`GM__${theme}-section-title`}>
              {vehicle.is_trailer ? "Cabezote Asociado" : "Trailer Asociado"}
            </h3>
            
            {hasRelatedVehicle ? (
              <div className={`GM__${theme}-related-info-card`}>
                <div className={`GM__${theme}-association-status connected`}>
                  <LinkIcon className={`GM__${theme}-status-icon linked`} />
                  <span className={`GM__${theme}-status-text`}>
                    Asociado correctamente
                  </span>
                </div>
                
                <div className={`GM__${theme}-related-vehicle-details`}>
                  <div className={`GM__${theme}-field-group`}>
                    <div className={`GM__${theme}-field`}>
                      <span className={`GM__${theme}-field-label`}>Placa:</span>
                      <span className={`GM__${theme}-field-value highlight`}>
                        {vehicle.relatedVehicle.data.plate}
                      </span>
                    </div>
                    <div className={`GM__${theme}-field`}>
                      <span className={`GM__${theme}-field-label`}>Tipo:</span>
                      <span className={`GM__${theme}-field-value`}>
                        {vehicle.relatedVehicle.data.vehicle_type_name}
                      </span>
                    </div>
                  </div>
                  
                  <div className={`GM__${theme}-field-group`}>
                    <div className={`GM__${theme}-field`}>
                      <span className={`GM__${theme}-field-label`}>Marca:</span>
                      <span className={`GM__${theme}-field-value`}>
                        {vehicle.relatedVehicle.data.brand_name || "No disponible"}
                      </span>
                    </div>
                    <div className={`GM__${theme}-field`}>
                      <span className={`GM__${theme}-field-label`}>Modelo:</span>
                      <span className={`GM__${theme}-field-value`}>
                        {vehicle.relatedVehicle.data.model} ({vehicle.relatedVehicle.data.year})
                      </span>
                    </div>
                  </div>

                  {/* Información adicional del vehículo relacionado */}
                  <div className={`GM__${theme}-field-group`}>
                    <div className={`GM__${theme}-field`}>
                      <span className={`GM__${theme}-field-label`}>Color:</span>
                      <span className={`GM__${theme}-field-value`}>
                        {vehicle.relatedVehicle.data.color || "No disponible"}
                      </span>
                    </div>
                    {vehicle.relatedVehicle.data.capacity && (
                      <div className={`GM__${theme}-field`}>
                        <span className={`GM__${theme}-field-label`}>Capacidad:</span>
                        <span className={`GM__${theme}-field-value`}>
                          {vehicle.relatedVehicle.data.capacity} ton
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Propietario del vehículo relacionado */}
                  {vehicle.relatedVehicle.data.owner && (
                    <div className={`GM__${theme}-related-owner-info`}>
                      <h4 className={`GM__${theme}-related-owner-title`}>
                        Propietario del {getRelatedVehicleTypeLabel}:
                      </h4>
                      <span className={`GM__${theme}-related-owner-name`}>
                        {formatFullName(vehicle.relatedVehicle.data.owner)}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className={`GM__${theme}-no-related-card`}>
                <div className={`GM__${theme}-association-status disconnected`}>
                  <UnlinkIcon className={`GM__${theme}-status-icon unlinked`} />
                  <span className={`GM__${theme}-status-text`}>
                    Sin asociación
                  </span>
                </div>
                
                <div className={`GM__${theme}-no-association-message`}>
                  {vehicle.is_trailer ? (
                    <div className={`GM__${theme}-trailer-message`}>
                      <h4>Este trailer no está asociado a ningún cabezote</h4>
                      <p>
                        Los trailers deben ser asociados desde la configuración del cabezote.
                        Para asociar este trailer:
                      </p>
                      <ul>
                        <li>Ve al perfil del cabezote deseado</li>
                        <li>Entra en modo de edición</li>
                        <li>Selecciona este trailer en la sección de gestión de trailer</li>
                      </ul>
                    </div>
                  ) : (
                    <div className={`GM__${theme}-vehicle-message`}>
                      <h4>Este cabezote no cuenta con su respectivo trailer</h4>
                      <p>
                        Puedes asociar un trailer desde el modo de edición:
                      </p>
                      <ul>
                        <li>Haz clic en el botón "Editar"</li>
                        <li>Ve a la sección "Gestión de Trailer"</li>
                        <li>Selecciona un trailer disponible</li>
                      </ul>
                      <div className={`GM__${theme}-edit-hint`}>
                        💡 <em>Tip: Si el trailer ya está asociado a otro vehículo, el sistema te permitirá reasignarlo.</em>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Mostrar información del vehículo relacionado para vehículos simples si existe */}
        {!vehicleRequiresRelated && showRelatedVehicle && vehicle.relatedVehicle && (
          <div className={`GM__${theme}-related-vehicle-section simple`}>
            <h3 className={`GM__${theme}-section-title`}>
              {vehicle.relatedVehicle.type === "trailer"
                ? "Trailer Relacionado"
                : "Cabezote Relacionado"}
            </h3>
            <div className={`GM__${theme}-related-info-card simple`}>
              <div className={`GM__${theme}-field-group`}>
                <div className={`GM__${theme}-field`}>
                  <span className={`GM__${theme}-field-label`}>Placa:</span>
                  <span className={`GM__${theme}-field-value`}>
                    {vehicle.relatedVehicle.data.plate}
                  </span>
                </div>
                <div className={`GM__${theme}-field`}>
                  <span className={`GM__${theme}-field-label`}>Tipo:</span>
                  <span className={`GM__${theme}-field-value`}>
                    {vehicle.relatedVehicle.data.vehicle_type_name}
                  </span>
                </div>
              </div>
              <div className={`GM__${theme}-field-group`}>
                <div className={`GM__${theme}-field`}>
                  <span className={`GM__${theme}-field-label`}>Marca:</span>
                  <span className={`GM__${theme}-field-value`}>
                    {vehicle.relatedVehicle.data.brand_name || "No disponible"}
                  </span>
                </div>
                <div className={`GM__${theme}-field`}>
                  <span className={`GM__${theme}-field-label`}>Modelo:</span>
                  <span className={`GM__${theme}-field-value`}>
                    {vehicle.relatedVehicle.data.model} (
                    {vehicle.relatedVehicle.data.year})
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Observaciones */}
        <div className={`GM__${theme}-observations-section`}>
          <h3 className={`GM__${theme}-section-title`}>Observaciones</h3>
          <div className={`GM__${theme}-observations-content`}>
            {commentData.length > 0 ? (
              <div className={`GM__${theme}-observations-cards-container`}>
                {/* Renderizar máximo 3 comentarios */}
                {commentData.slice(0, 3).map((comment) => (
                  <div 
                    key={comment.id} 
                    className={`GM__${theme}-observation-card`}
                    onClick={() => handleOpenDrawer("comments")}
                  >
                    <div className={`GM__${theme}-observation-card-content`}>
                      <span className={`GM__${theme}-observation-title`}>
                        {comment.title || 'Sin título'}
                      </span>
                    </div>
                  </div>
                ))}
                
                {/* Card de "Ver todos" como cuarta card */}
                <div 
                  className={`GM__${theme}-observation-card ${`GM__${theme}-add-more-card`}`}
                  onClick={() => handleOpenDrawer("comments")}
                >
                  <div className={`GM__${theme}-observation-card-content`}>
                    <span className={`GM__${theme}-add-more-text`}>
                      Ver todos ({commentData.length})
                    </span>
                  </div>
                </div>
              </div>
            ) : (
              <div className={`GM__${theme}-no-observations`}>
                <div 
                  className={`GM__${theme}-observation-card ${`GM__${theme}-add-comment-card`}`}
                  onClick={() => handleOpenDrawer("comments")}
                >
                  <div className={`GM__${theme}-observation-card-content`}>
                    <span className={`GM__${theme}-add-comment-text`}>
                      Agregar comentario
                    </span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <CommentsLocationsDrawer
        open={drawerOpen}
        onClose={handleCloseDrawer}
        type={drawerType}
        data={commentData}
        onEdit={handleEdit}
        onCreate={handleCreate}
        onDelete={handleDelete}
        theme={theme}
      />
    </div>
  );
};

VehicleProfileInfo.propTypes = {
  vehicle: PropTypes.shape({
    id: PropTypes.string.isRequired,
    operation_id: PropTypes.string,
    plate: PropTypes.string.isRequired,
    type_name: PropTypes.string,
    type_category: PropTypes.string,
    color: PropTypes.string,
    model: PropTypes.string,
    year: PropTypes.number,
    brand_name: PropTypes.string,
    capacity: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    operation_name: PropTypes.string,
    is_trailer: PropTypes.bool,
    belongs_to_operation: PropTypes.bool,
    primary_phone_number: PropTypes.string,
    primary_phone_code: PropTypes.string,
    second_phone: PropTypes.string,
    secondary_phone_code: PropTypes.string,
    nationality_country: PropTypes.string,
    contract_start_date: PropTypes.string,
    contract_end_date: PropTypes.string,
    documents: PropTypes.array.isRequired,
    owner: PropTypes.shape({
      id: PropTypes.string,
      first_name: PropTypes.string,
      first_last_name: PropTypes.string,
      second_last_name: PropTypes.string,
      email: PropTypes.string,
      document_number: PropTypes.string,
      primary_phone_number: PropTypes.string,
      primary_phone_code: PropTypes.string,
      secondary_phone_number: PropTypes.string,
      secondary_phone_code: PropTypes.string,
      nationality_country: PropTypes.string,
    }),
    operator: PropTypes.shape({
      id: PropTypes.string,
      first_name: PropTypes.string,
      first_last_name: PropTypes.string,
      second_last_name: PropTypes.string,
      email: PropTypes.string,
      document_number: PropTypes.string,
      primary_phone_number: PropTypes.string,
      primary_phone_code: PropTypes.string,
      secondary_phone_number: PropTypes.string,
      secondary_phone_code: PropTypes.string,
      nationality_country: PropTypes.string,
    }),
    relatedVehicle: PropTypes.object,
  }).isRequired,
  theme: PropTypes.oneOf(["dark", "light"]).isRequired,
  onEditClick: PropTypes.func,
  showRelatedVehicle: PropTypes.bool,
  isSimpleVehicle: PropTypes.bool,
};

export default VehicleProfileInfo;